import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../styles/projLoginPage.css";
import "../styles/RegisterPage.css";
import { API_BASE } from "../config";
import ServerCaptchaWidget from "./ServerCaptchaWidget";
import SECURITY_QUESTIONS from "../config/securityQuestions.json";
import { YEAR_OPTIONS, DISPLAY_YEAR_OPTIONS } from "../data/vocabYearGroups";

// ── Password strength checks ──
const STRENGTH_RULES = [
  { label: "At least 8 characters",      test: (p) => p.length >= 8 },
  { label: "At least 1 uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { label: "At least 1 number",          test: (p) => /[0-9]/.test(p) },
  { label: "At least 1 special character",test: (p) => /[^a-zA-Z0-9]/.test(p) },
];

function StrengthCheck({ ok, label }) {
  return (
    <div className={`reg-check ${ok ? "reg-check-ok" : ""}`}>
      <span className="reg-check-icon">{ok ? "✓" : "○"}</span>
      <span>{label}</span>
    </div>
  );
}

// ── Client-side validators ──
function validateUsername(v) {
  if (!v.trim()) return "Username is required.";
  if (/\s/.test(v)) return "Username cannot contain spaces.";
  if (!/^[a-zA-Z0-9_-]{6,50}$/.test(v.trim()))
    return "6–50 characters, letters, numbers, hyphens and underscores only.";
  return null;
}

function validateDisplayName(v) {
  if (!v || !v.trim()) return null; // optional
  if (v.trim().length < 2) return "Profile name must be at least 2 characters.";
  if (v.trim().length > 50) return "Profile name must be 50 characters or fewer.";
  return null;
}

function validateEmail(v) {
  if (!v.trim()) return "Email address is required.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim()))
    return "Please enter a valid email address.";
  return null;
}

function validatePassword(v) {
  if (!v) return "Password is required.";
  if (v.length < 8)          return "At least 8 characters required.";
  if (!/[A-Z]/.test(v))      return "At least 1 uppercase letter required.";
  if (!/[0-9]/.test(v))      return "At least 1 number required.";
  if (!/[^a-zA-Z0-9]/.test(v)) return "At least 1 special character required.";
  return null;
}

function validateConfirm(pw, confirm) {
  if (!confirm) return "Please confirm your password.";
  if (pw !== confirm) return "Passwords do not match.";
  return null;
}

function validateQuestion(v) {
  if (!v) return "Please select a security question.";
  return null;
}

function validateAnswer(v) {
  if (!v.trim()) return "Please provide a security answer.";
  if (v.trim().length < 2) return "Answer must be at least 2 characters.";
  return null;
}

function validateAgeGroup(v) {
  if (!v) return "Please select your age group.";
  return null;
}

export default function RegisterPage() {
  useEffect(() => { document.title = "Register — Owllet Reading Quest"; }, []);
  const navigate = useNavigate();

  const [fields, setFields] = useState({
    username: "", displayName: "", email: "", password: "", confirmPassword: "",
    securityQuestion: "", securityAnswer: "", ageGroup: "below_16", preferredTier: "",
  });
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [formLoadedAt, setFormLoadedAt] = useState(null);
  const [captchaOk, setCaptchaOk] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [isTeacher, setIsTeacher] = useState(false);
  const captchaRef = useRef(null);

  useEffect(() => { setFormLoadedAt(Date.now()); }, []);


  const errors = {
    username:         validateUsername(fields.username),
    displayName:      validateDisplayName(fields.displayName),
    email:            validateEmail(fields.email),
    password:         validatePassword(fields.password),
    confirmPassword:  validateConfirm(fields.password, fields.confirmPassword),
    securityQuestion: validateQuestion(fields.securityQuestion),
    securityAnswer:   validateAnswer(fields.securityAnswer),
  };

  const hasErrors = Object.values(errors).some(Boolean);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const sanitized = name === "username" ? value.replace(/\s/g, "") : value;
    setFields((f) => ({ ...f, [name]: sanitized }));
  };

  const handleBlur = (e) => {
    setTouched((t) => ({ ...t, [e.target.name]: true }));
  };

  const fieldClass = (name) =>
    `lp-input-wrap${touched[name] && errors[name] ? " lp-input-err" : ""}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ username: true, email: true, password: true, confirmPassword: true, securityQuestion: true, securityAnswer: true });
    if (hasErrors) return;
    if (!captchaOk) { setServerError("Please complete the security check."); return; }

    setServerError("");
    setIsLoading(true);
    const { token: captchaToken, answer: captchaAnswer } = captchaRef.current?.getChallenge() ?? {};

    try {
      const res = await fetch(`${API_BASE}/api/auth/register`, {
        method:      "POST",
        headers:     { "Content-Type": "application/json" },
        credentials: "include",
        body:        JSON.stringify({
          username: fields.username.trim(),
          displayName: fields.displayName.trim() || null,
          email: fields.email.trim(),
          password: fields.password,
          securityQuestion: fields.securityQuestion,
          securityAnswer: fields.securityAnswer.trim(),
          ageGroup: fields.ageGroup,
          preferredTier: fields.preferredTier || null,
          isTeacher,
          captchaToken,
          captchaAnswer,
          website: honeypot,
          formLoadedAt,
        }),
      });
      const data = await res.json();
      if (data.success && data.pendingActivation) {
        navigate("/registrationConfirmed", { state: { username: data.username, email: data.email, owlletId: data.owlletId } });
      } else {
        setServerError(data.message || "Registration failed. Please try again.");
        captchaRef.current?.refresh();
        setCaptchaOk(false);
      }
    } catch {
      setServerError("Something went wrong. Please try again.");
      captchaRef.current?.refresh();
      setCaptchaOk(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="lp-container reg-container">
      <div className="lp-card reg-card">

        {/* Branding — horizontal to save vertical space */}
        <div className="lp-brand reg-brand">
          <div className="lp-logo reg-logo">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
          </div>
          <div>
            <h1 className="lp-title">Create Account</h1>
            <p className="lp-subtitle">Join Reading Quest</p>
          </div>
        </div>

        {/* Honeypot */}
        <div className="contact-hp-field" aria-hidden="true">
          <label htmlFor="reg-website">Website</label>
          <input id="reg-website" type="text" value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" />
        </div>

        <form className="lp-form reg-form" onSubmit={handleSubmit} noValidate>
          {serverError && <div className="lp-error-banner">{serverError}</div>}

          {/* Row 1: Username + Profile Name */}
          <div className="reg-row">
            <div className="lp-field">
              <label htmlFor="reg-username">Username <span className="reg-required">*</span></label>
              <div className={fieldClass("username")}>
                <svg className="lp-field-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <input id="reg-username" name="username" type="text"
                  placeholder="min. 6 characters, no spaces" autoComplete="username"
                  value={fields.username} onChange={handleChange} onBlur={handleBlur} />
              </div>
              {touched.username && errors.username && <p className="lp-err-text">{errors.username}</p>}
              <p className="reg-hint">Used to log in — no spaces allowed.</p>
            </div>

            <div className="lp-field">
              <label htmlFor="reg-display-name">Profile Name <span className="reg-optional">(optional)</span></label>
              <div className={fieldClass("displayName")}>
                <svg className="lp-field-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M5.121 17.804A8.966 8.966 0 0112 15c2.021 0 3.88.666 5.379 1.785M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <input id="reg-display-name" name="displayName" type="text"
                  placeholder="Your first name or nickname"
                  value={fields.displayName} onChange={handleChange} onBlur={handleBlur} />
              </div>
              {touched.displayName && errors.displayName && <p className="lp-err-text">{errors.displayName}</p>}
              <p className="reg-hint">Shown on your profile — can be your first name or a nickname.</p>
            </div>
          </div>

          {/* Row 2: Email */}
          <div className="reg-row">
            <div className="lp-field">
              <label htmlFor="reg-email">Email <span className="reg-required">*</span></label>
              <div className={fieldClass("email")}>
                <svg className="lp-field-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <input id="reg-email" name="email" type="email"
                  placeholder="your@email.com" autoComplete="email"
                  value={fields.email} onChange={handleChange} onBlur={handleBlur} />
              </div>
              {touched.email && errors.email && <p className="lp-err-text">{errors.email}</p>}
              <p className="reg-hint">Used to activate your account and for password reset.</p>
            </div>
          </div>

          {/* Row 3: Password + Confirm */}
          <div className="reg-row">
            <div className="lp-field">
              <label htmlFor="reg-password">Password <span className="reg-required">*</span></label>
              <div className={fieldClass("password")}>
                <svg className="lp-field-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <input id="reg-password" name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="min. 8 characters" autoComplete="new-password"
                  value={fields.password} onChange={handleChange} onBlur={handleBlur} />
                <button type="button" className="lp-eye-btn"
                  onClick={() => setShowPassword((v) => !v)} tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}>
                  {showPassword
                    ? <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                    : <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  }
                </button>
              </div>
              {touched.password && errors.password && <p className="lp-err-text">{errors.password}</p>}
            </div>

            <div className="lp-field">
              <label htmlFor="reg-confirm">Confirm password <span className="reg-required">*</span></label>
              <div className={fieldClass("confirmPassword")}>
                <svg className="lp-field-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <input id="reg-confirm" name="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="Repeat password" autoComplete="new-password"
                  value={fields.confirmPassword} onChange={handleChange} onBlur={handleBlur} />
              </div>
              {touched.confirmPassword && errors.confirmPassword && <p className="lp-err-text">{errors.confirmPassword}</p>}
            </div>
          </div>

          {/* Password strength — full width, below the row */}
          {fields.password && (
            <div className="reg-strength reg-strength-grid">
              {STRENGTH_RULES.map((r) => (
                <StrengthCheck key={r.label} ok={r.test(fields.password)} label={r.label} />
              ))}
            </div>
          )}

          {/* Security question */}
          <div className="lp-field">
            <label htmlFor="reg-sq">Security question <span className="reg-required">*</span></label>
            <div className={`reg-select-wrap${touched.securityQuestion && errors.securityQuestion ? " lp-input-err" : ""}`}>
              <svg className="lp-field-icon reg-select-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <select id="reg-sq" name="securityQuestion" className="reg-select"
                value={fields.securityQuestion} onChange={handleChange} onBlur={handleBlur}>
                <option value="">— Select —</option>
                {SECURITY_QUESTIONS.map((q) => (
                  <option key={q} value={q}>{q}</option>
                ))}
              </select>
            </div>
            {touched.securityQuestion && errors.securityQuestion && <p className="lp-err-text">{errors.securityQuestion}</p>}
          </div>

          {/* Security answer */}
          <div className="lp-field">
            <label htmlFor="reg-sa">Your answer <span className="reg-required">*</span></label>
            <div className={fieldClass("securityAnswer")}>
              <svg className="lp-field-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <input id="reg-sa" name="securityAnswer" type="text"
                placeholder="Case-insensitive" autoComplete="off"
                value={fields.securityAnswer} onChange={handleChange} onBlur={handleBlur} />
            </div>
            <p className="reg-hint">Stored hashed — not readable by anyone.</p>
            {touched.securityAnswer && errors.securityAnswer && <p className="lp-err-text">{errors.securityAnswer}</p>}
          </div>

          {/* Difficulty Tier */}
          <div className="lp-field">
            <label htmlFor="reg-tier">Starting difficulty tier <span className="reg-hint">(optional)</span></label>
            <div className="reg-select-wrap">
              <svg className="lp-field-icon reg-select-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <select
                id="reg-tier"
                name="preferredTier"
                className="reg-select"
                value={fields.preferredTier}
                onChange={handleChange}
                disabled={!fields.ageGroup}
              >
                <option value="">— Select a tier —</option>
                {DISPLAY_YEAR_OPTIONS.map((opt) => (
                  <option key={opt.tier} value={String(opt.tier)}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <p className="reg-hint">Pre-selects your difficulty in all game modes. You can change this anytime in Settings.</p>
          </div>

          {/* Teacher account toggle */}
          <div className="reg-teacher-toggle">
            <label className="reg-teacher-label">
              <input
                type="checkbox"
                checked={isTeacher}
                onChange={e => setIsTeacher(e.target.checked)}
                className="reg-teacher-checkbox"
              />
              <span className="reg-teacher-text">
                <strong>Register as a Teacher / Tutor account</strong>
                <span className="reg-teacher-hint">
                  Teacher accounts can create a class, accept students and assign tasks.
                  Student accounts are the default.
                </span>
              </span>
            </label>
          </div>

          {/* CAPTCHA */}
          <div className="lp-field">
            <ServerCaptchaWidget ref={captchaRef} onVerify={(ok) => setCaptchaOk(ok)} />
          </div>

          <button type="submit" className="lp-submit" disabled={isLoading}>
            {isLoading ? "Creating account…" : "Create Account"}
          </button>
        </form>

        <p className="fp-back-link" style={{ marginTop: "1rem" }}>
          Already have an account? <Link to="/projLoginPage">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
